/**
 * The tappable pills beside the notes box, and their editing.
 *
 * Each pill writes its own label as a note, so they are kept on the server
 * per gardener and in their order, to look the same on every device.
 *
 * Endpoints:
 * - GET    /note-shortcuts → NoteShortcut[] (a bare list).
 * - POST   /note-shortcuts { label, colorKey } → { shortcut_id }.
 * - PATCH  /note-shortcuts/:id { label?, colorKey? }.
 * - PUT    /note-shortcuts/order { order: number[] }.
 * - DELETE /note-shortcuts/:id.
 *
 * Who uses it: features/plant-list QuickNoteChips (the pills), and the
 * profile's NoteShortcutSettings and UserProfile. Caching:
 * [NOTE_SHORTCUTS_QUERY_KEY] (['noteShortcuts']; NOTES_SHORTCUTS_STALE_MS
 * on the card), invalidated after each edit. Auth: the signed-in person's
 * own.
 *
 * @module noteShortcuts
 */

import { apiClient } from './apiClient';

/**
 * One tappable pill.
 *
 * The label is the note text itself: tapping "Watered" writes a note that says
 * Watered. There is no link back from a note to the pill that wrote it, so
 * renaming a pill never rewrites what a gardener has already recorded.
 */
export interface NoteShortcut {
  /** Negative for the starting four on a database without the labels table. */
  shortcut_id: number;
  /** Its words, which are the note it writes. */
  label: string;
  /** A key from the colour palette. */
  color_key: string;
  /** Its place in the row. */
  sort_order: number;
}

/**
 * This gardener's pills, in the order they are shown.
 *
 * A gardener with none is given the starting four by the server, so this never
 * comes back empty on a first visit. On a database where the labels migration
 * has not been run the same four arrive with negative ids: they still write
 * notes, they simply cannot be edited yet.
 *
 * @returns The pills, in order; [] if the answer is not a list.
 */
export async function getNoteShortcuts(): Promise<NoteShortcut[]> {
  const response = await apiClient.get<NoteShortcut[]>('/note-shortcuts');
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * Adds a pill. The server refuses a duplicate label and a twelfth pill.
 *
 * @param label - Its words, which are the note it writes.
 * @param colorKey - A key from the colour palette; leave it out and the server picks the pill's colour.
 * @returns The new pill's id.
 */
export async function createNoteShortcut(label: string, colorKey?: string): Promise<number> {
  const response = await apiClient.post<{ shortcut_id: number }>(
    '/note-shortcuts', { label, colorKey }
  );
  return response.data.shortcut_id;
}

/**
 * Renames a pill or repaints it. Omitted fields are left alone.
 *
 * @param shortcutId - The pill.
 * @param edit - The new label, colour, or both.
 */
export async function updateNoteShortcut(
  shortcutId: number,
  edit: { label?: string; colorKey?: string }
): Promise<void> {
  await apiClient.patch(`/note-shortcuts/${shortcutId}`, edit);
}

/**
 * Keeps the pills in this order.
 *
 * @param shortcutIds - Every pill, in the order wanted.
 */
export async function reorderNoteShortcuts(shortcutIds: number[]): Promise<void> {
  await apiClient.put('/note-shortcuts/order', { order: shortcutIds });
}

/**
 * Removes a pill.
 *
 * The notes it has written stay where they are. They are sentences somebody
 * wrote about their garden, not instances of a button.
 *
 * @param shortcutId - The pill.
 */
export async function deleteNoteShortcut(shortcutId: number): Promise<void> {
  await apiClient.delete(`/note-shortcuts/${shortcutId}`);
}
